import { Plus, X } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout, Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { FieldMapper } from "@/features/trigger-field-mapper";
import { TriggerRouting } from "@/features/trigger-routing";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fieldSources, requestState, standardUses, type RequestState } from "@/lib/field-mapping";
import { sameValues } from "@/lib/form";
import {
  useCampaigns, useCreateTrigger, useDispositions, useFormDesign, useTenants, useTriggerInbox, useTriggerRequest,
  useUpdateTrigger,
} from "@/lib/queries";
import type { AdminForm, Disposition, Trigger, TriggerCreated } from "@/lib/types";
import {
  fillsFields,
  writesRecord,
  EMAIL_RECEIVED, EVERY_CAMPAIGN, FIELD_WORDS, LEAD_OUTCOME, MAX_IDEMPOTENCY_PATHS, MESSAGE_RECEIVED, MESSAGE_REQUIRED, MESSAGE_SEND,
  MESSAGE_STATUS, NEW_TRIGGER, SECRET_KINDS, STATUS_REQUIRED, TRIGGER_ACTIONS, TRIGGER_REPLIES, actionTerm,
  fieldTerm, outcomeEffect, outcomesGone, replyText, requestPaths, secretKindText, takesCampaign, toggleOutcome,
  triggerChanges, triggerPayload, triggerProblem, triggerValues, withAction, type MappingField,
  type TriggerValues,
} from "@/lib/triggers";

/** Radix's Select cannot hold an empty value, so every campaign has a value of its own here. */
const ALL = "all";
const PATHS_LIST = "trigger-request-paths";
/** The contact form when it cannot be read (a superadmin with no tenant chosen yet): no fields to pick. */
const NO_DESIGN: AdminForm = { entity: "lead", title: "Contact", sections: [] };

/**
 * Make a trigger, or edit one (ADR-021): what it does, which campaign it touches, and where in a
 * request it finds what the action needs.
 *
 * A new trigger starts as a zap's new-lead trigger with its fields named as "How to call it" tells a
 * zap to name them, so the common case is a name and a campaign. Consent is not asked for (ACL-575,
 * platform ADR-194): an older trigger's consent mapping and wording are kept as they are. On an edit,
 * the paths in the trigger's newest request are offered in every path field: the way to map a sender
 * is to send one request to a trigger and read what arrived.
 *
 * A text trigger (ADR-095 in platform) asks for other things: where a request carries the text, who
 * delivers it, and what the sender is answered. It has no campaign and names no lead, since a text's
 * inbox is its number's. Choosing the action fills in Twilio's own field names (`withAction`).
 *
 * A new lead's custom fields (ACL-375, ADR-119 in platform) are mapped here too, by `FieldMapper`: the
 * newest request's keys against the contact form's fields, saved with the rest of the trigger. Both are
 * read once when the form opens, so a request arriving mid-edit does not reshuffle the rows.
 *
 * An outcome trigger (ADR-021 in platform, amended 2026-10-10) sets only the outcomes ticked here, out
 * of the tenant's own catalog: what a sender may do to a lead is this form's to say, and nothing else is.
 */
export function TriggerFormDialog({
  open, onOpenChange, trigger, onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  trigger?: Trigger;
  /** After a create: the trigger with its secret, which is shown this once. */
  onCreated?: (created: TriggerCreated) => void;
}) {
  const toast = useToast();
  const { user: actor } = useAuth();
  const create = useCreateTrigger();
  const update = useUpdateTrigger();
  const campaigns = useCampaigns({}, { enabled: open });
  const isSuperadmin = actor?.role === "superadmin";
  const { data: tenants } = useTenants({ enabled: open && isSuperadmin && !trigger });
  const pickTenant = isSuperadmin && !trigger && (tenants?.length ?? 0) > 1;
  // The newest request, for the paths it holds.
  const inbox = useTriggerInbox(open ? trigger?.id : undefined);
  const newest = useTriggerRequest(open ? trigger?.id : undefined, inbox.data?.items[0]?.id);
  const suggestions = newest.data?.raw ? requestPaths(newest.data.raw) : [];

  const [values, setValues] = React.useState<TriggerValues>(NEW_TRIGGER);
  // What the form was loaded with, which an edit is measured against.
  const [loaded, setLoaded] = React.useState<TriggerValues>(NEW_TRIGGER);
  const [tenantId, setTenantId] = React.useState<number | null>(null);
  const [touched, setTouched] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  // Loaded when it opens, and for another trigger, but not each time the same trigger is fetched
  // again: a request arriving changes its `last_received_at` while somebody edits, and reloading
  // then wiped what they had typed.
  const latest = React.useRef(trigger);
  latest.current = trigger;
  React.useEffect(() => {
    if (!open) return;
    const start = latest.current ? triggerValues(latest.current) : NEW_TRIGGER;
    setValues(start);
    setLoaded(start);
    setTenantId(null);
    setTouched(false);
    setFormError(null);
  }, [open, trigger?.id]);

  const set = <K extends keyof TriggerValues>(key: K, value: TriggerValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));
  const setPath = (field: MappingField, path: string) =>
    setValues((current) => ({ ...current, mapping: { ...current.mapping, [field]: path } }));

  const tenant = trigger?.tenant_id ?? tenantId ?? actor?.tenant_id ?? null;
  // The tenant's outcomes, for a trigger that sets one: read only for that action, and for the
  // tenant the trigger is, or will be, in.
  const catalog = useDispositions(tenant ?? 0, { enabled: open && values.action === LEAD_OUTCOME && tenant !== null });
  const tenantCampaigns = (campaigns.data ?? []).filter((c) => tenant === null || c.tenant_id === tenant);
  const problem = triggerProblem(values);
  const changed = !sameValues(values, loaded) || tenantId !== null;
  const action = values.action;
  const enlist = action === "lead.enlist";
  const updatesLead = action === "lead.update";
  const capture = action === "capture";
  const message = action === MESSAGE_RECEIVED;
  const report = action === MESSAGE_STATUS;
  const sending = action === MESSAGE_SEND;
  const email = action === EMAIL_RECEIVED;
  const basic = values.secret_kind === "basic";
  // The custom fields' two inputs: the contact form (the trigger's tenant, which a superadmin names;
  // everyone else's is their own) and the newest request, frozen the first time it is known. A read that
  // failed is not frozen for good: the inbox's own poll or a refocus tries again, and that answer is taken.
  const design = useFormDesign("lead", isSuperadmin ? (trigger?.tenant_id ?? tenantId) : null, {
    enabled: open && fillsFields(action),
  });
  const [frozen, setFrozen] = React.useState<{ state: RequestState; doc: Record<string, unknown> | null } | undefined>(undefined);
  const liveRequest: RequestState | undefined = !trigger
    ? "none"
    : requestState({
        failed: (inbox.isError && inbox.data === undefined) || (newest.isError && newest.data === undefined),
        count: inbox.data?.items.length,
        raw: newest.data ? newest.data.raw : undefined,
      });
  React.useEffect(() => {
    if (!open) {
      setFrozen(undefined);
      return;
    }
    if (liveRequest !== undefined && (frozen === undefined || (frozen.state === "unreadable" && liveRequest !== "unreadable"))) {
      setFrozen({ state: liveRequest, doc: liveRequest === "read" ? (newest.data?.raw ?? null) : null });
    }
  }, [open, liveRequest, newest.data, frozen]);
  const uses = React.useMemo(() => standardUses({ action: values.action, mapping: triggerPayload(values).mapping }), [values]);
  const originalFields = React.useMemo(() => fieldSources(trigger?.mapping?.fields), [trigger]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    setFormError(null);
    if (problem) return;
    if (pickTenant && tenantId === null) {
      setFormError("Choose the tenant whose leads it acts on.");
      return;
    }
    try {
      if (trigger) {
        const changes = triggerChanges(values, trigger);
        if (Object.keys(changes).length > 0) await update.mutateAsync({ id: trigger.id, ...changes });
        toast.success("Trigger saved", values.name.trim());
        onOpenChange(false);
      } else {
        const created = await create.mutateAsync({
          ...triggerPayload(values),
          ...(pickTenant && tenantId !== null ? { tenant_id: tenantId } : {}),
        });
        onOpenChange(false);
        onCreated?.(created);
      }
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the trigger.");
    }
  };

  const pathField = (field: MappingField, required = false, placeholder?: string) => (
    <Field
      key={field}
      label={fieldTerm(action, field).label}
      htmlFor={`trigger-path-${field}`}
      required={required}
      hint={fieldTerm(action, field).description || undefined}
    >
      <Input
        autoComplete="off"
        className="font-mono"
        list={PATHS_LIST}
        placeholder={placeholder ?? "a path, like contact.phone"}
        value={values.mapping[field]}
        onChange={(e) => setPath(field, e.target.value)}
      />
    </Field>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer" size="lg">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{trigger ? "Edit trigger" : "New trigger"}</DialogTitle>
          <DialogDescription>
            A URL another system calls when something happens there, and what the platform does about it.
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-5">
            {formError && <Callout tone="danger">{formError}</Callout>}
            {touched && problem && !formError && <Callout tone="danger">{problem}</Callout>}

            <div className="space-y-4">
              <Field label="Name" htmlFor="trigger-name" required hint="How this console lists it. Unique among your triggers.">
                <Input
                  autoComplete="off"
                  maxLength={200}
                  placeholder="Zapier: new leads from the intake form"
                  invalid={touched && !values.name.trim()}
                  value={values.name}
                  onChange={(e) => set("name", e.target.value)}
                />
              </Field>
              <Field label="Description" htmlFor="trigger-description" hint="Which zap or workflow calls it, for whoever reads this next.">
                <Textarea
                  rows={2}
                  className="min-h-14 font-sans text-sm"
                  maxLength={2000}
                  value={values.description}
                  onChange={(e) => set("description", e.target.value)}
                />
              </Field>

              {pickTenant && (
                <Field label="Tenant" htmlFor="trigger-tenant" required hint="Whose leads it acts on.">
                  <Select value={tenantId?.toString() ?? ""} onValueChange={(next) => setTenantId(Number(next))}>
                    <SelectTrigger id="trigger-tenant">
                      <SelectValue placeholder="Choose a tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      {(tenants ?? []).map((t) => (
                        <SelectItem key={t.id} value={String(t.id)}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}

              <Field label="What it does" htmlFor="trigger-action" required hint={actionTerm(action).description}>
                <Select value={action} onValueChange={(next) => setValues((current) => withAction(current, next, loaded))}>
                  <SelectTrigger id="trigger-action">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRIGGER_ACTIONS.map((a) => (
                      <SelectItem key={a} value={a} description={actionTerm(a).description}>
                        {actionTerm(a).label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {!capture && takesCampaign(action) && !writesRecord(action) && (
                <Field
                  label="Campaign"
                  htmlFor="trigger-campaign"
                  required={enlist}
                  hint={
                    enlist
                      ? "The campaign whose dial list new leads go on."
                      : "Only this campaign's dial list, or every campaign the lead is on. Do-not-call is the lead's own, on every campaign."
                  }
                >
                  <Select
                    value={values.campaign_id || ALL}
                    onValueChange={(next) => set("campaign_id", next === ALL ? EVERY_CAMPAIGN : next)}
                  >
                    <SelectTrigger id="trigger-campaign" invalid={touched && enlist && !values.campaign_id}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>{enlist ? "Choose a campaign" : "Every campaign"}</SelectItem>
                      {tenantCampaigns.map((campaign) => (
                        <SelectItem key={campaign.id} value={campaign.id}>
                          {campaign.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </div>

            {message && (
              <Section
                title="Where a request carries the text"
                description={
                  suggestions.length > 0
                    ? "Paths into what the provider sends. The fields of this trigger's newest request are suggested in each box."
                    : "Paths into what the provider sends. Twilio posts a form whose fields are From, To, Body, MessageSid and NumMedia."
                }
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  {pathField("from", MESSAGE_REQUIRED.includes("from"), "From")}
                  {pathField("to", MESSAGE_REQUIRED.includes("to"), "To")}
                </div>
                {pathField("text", MESSAGE_REQUIRED.includes("text"), "Body")}
                <div className="grid gap-4 sm:grid-cols-2">
                  {pathField("message_id", MESSAGE_REQUIRED.includes("message_id"), "MessageSid")}
                  {pathField("media_count", false, "NumMedia")}
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Provider"
                    htmlFor="trigger-provider"
                    required
                    hint="Who delivers the texts. Kept on each one: twilio."
                  >
                    <Input
                      autoComplete="off"
                      className="font-mono"
                      maxLength={40}
                      placeholder="twilio"
                      invalid={touched && !values.provider.trim()}
                      value={values.provider}
                      onChange={(e) => set("provider", e.target.value)}
                    />
                  </Field>
                  <Field label="What it answers" htmlFor="trigger-reply" hint={replyText(values.reply).description}>
                    <Select value={values.reply} onValueChange={(next) => set("reply", next)}>
                      <SelectTrigger id="trigger-reply">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TRIGGER_REPLIES.map((reply) => (
                          <SelectItem key={reply} value={reply} description={replyText(reply).description}>
                            {replyText(reply).label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <p className="text-xs text-text-muted">
                  The text lands in the inbox its number names: a campaign's, a rep's own, or Unassigned. The
                  sender is looked up as a caller is, and a text that is only STOP marks the lead do-not-call.
                </p>
              </Section>
            )}

            {report && (
              <Section
                title="Where a report carries the text's id and its status"
                description={
                  suggestions.length > 0
                    ? "Paths into what the provider sends. The fields of this trigger's newest request are suggested in each box."
                    : "Paths into what the provider sends. Twilio posts a form whose fields are MessageSid, MessageStatus and ErrorCode."
                }
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  {pathField("message_id", STATUS_REQUIRED.includes("message_id"), "MessageSid")}
                  {pathField("status", STATUS_REQUIRED.includes("status"), "MessageStatus")}
                </div>
                {pathField("error_code", false, "ErrorCode")}
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Provider"
                    htmlFor="trigger-provider"
                    required
                    hint="Whose reports these are. A report finds its text by the provider and the id together: twilio."
                  >
                    <Input
                      autoComplete="off"
                      className="font-mono"
                      maxLength={40}
                      placeholder="twilio"
                      invalid={touched && !values.provider.trim()}
                      value={values.provider}
                      onChange={(e) => set("provider", e.target.value)}
                    />
                  </Field>
                  <Field label="What it answers" htmlFor="trigger-reply" hint={replyText(values.reply).description}>
                    <Select value={values.reply} onValueChange={(next) => set("reply", next)}>
                      <SelectTrigger id="trigger-reply">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TRIGGER_REPLIES.map((reply) => (
                          <SelectItem key={reply} value={reply} description={replyText(reply).description}>
                            {replyText(reply).label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <p className="text-xs text-text-muted">
                  A report moves the text it names to delivered, or to failed with the provider's code. Reports
                  arrive in any order, so a text only moves forward.
                </p>
              </Section>
            )}

            {email && (
              <Section
                title="Who posts the emails"
                description="SendGrid's Inbound Parse posts each email to this trigger's own URL, which ends in /email. That route reads the email itself, so nothing is mapped."
              >
                <Field
                  label="Provider"
                  htmlFor="trigger-provider"
                  required
                  hint="Who posts the emails. Kept on each one: sendgrid."
                >
                  <Input
                    autoComplete="off"
                    className="font-mono"
                    maxLength={40}
                    placeholder="sendgrid"
                    invalid={touched && !values.provider.trim()}
                    value={values.provider}
                    onChange={(e) => set("provider", e.target.value)}
                  />
                </Field>
                <p className="text-xs text-text-muted">
                  The email joins the thread its reply address names, or the thread of the email of ours it answers.
                  One to an unknown reply address of ours goes to Unassigned with no contact; anything else is not
                  kept, and says why under Requests. The Email settings make this trigger and show its URL whole.
                </p>
              </Section>
            )}

            {sending && (
              <Section
                title="The text, and the conversation it answers"
                description={
                  suggestions.length > 0
                    ? "Paths into what the sender sends. The fields of this trigger's newest request are suggested in each box."
                    : "Paths into what the sender sends: text for a field named text, message.text for one inside message."
                }
              >
                {pathField("text", true, "text")}
                <div className="grid gap-4 sm:grid-cols-2">
                  {pathField("conversation_id", false, "conversation_id")}
                  {pathField("from", false, "from")}
                </div>
                <p className="text-xs text-text-muted">
                  The text gets every check a rep's text gets: do-not-call, a STOP, the person's 8 am to 9 pm
                  and the day's limit. One that may not leave is refused, and the trigger's page says why. A request
                  names the conversation to answer, or a lead, or both: the conversation is used when it is there.
                </p>
              </Section>
            )}

            {!capture && !message && !report && !email && (
              <Section
                title={sending ? "Or where a request names the lead" : "Where a request names the lead"}
                description={
                  suggestions.length > 0
                    ? "Paths into what the sender sends. The fields of this trigger's newest request are suggested in each box."
                    : "Paths into what the sender sends: phone for a field named phone, contact.phone for one inside contact."
                }
              >
                {pathField("phone", enlist)}
                <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
                  {pathField("source_ref", enlist)}
                  <Field
                    label="Its source"
                    htmlFor="trigger-lead-source"
                    required={enlist || Boolean(values.mapping.source_ref.trim())}
                    hint="What those ids belong to: zapier, ghl."
                  >
                    <Input
                      autoComplete="off"
                      className="font-mono"
                      maxLength={100}
                      value={values.lead_source}
                      onChange={(e) => set("lead_source", e.target.value)}
                    />
                  </Field>
                </div>
                {!enlist && pathField("lead_id")}
              </Section>
            )}

            {action === "lead.hold" && (
              <Section title="The hold" description="Until the time a request names, or for a number of hours when it names none.">
                {pathField("until", false, "appointment.end_time")}
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Hours" htmlFor="trigger-hours" hint="24 when left empty.">
                    <Input inputMode="numeric" value={values.hours} onChange={(e) => set("hours", e.target.value)} />
                  </Field>
                  <Field label="Reason" htmlFor="trigger-reason" hint="appointment when left empty.">
                    <Input className="font-mono" value={values.reason} onChange={(e) => set("reason", e.target.value)} />
                  </Field>
                  <Field label="Times read in" htmlFor="trigger-timezone" hint="For a time with no zone.">
                    <Input
                      className="font-mono"
                      placeholder="America/Los_Angeles"
                      value={values.timezone}
                      onChange={(e) => set("timezone", e.target.value)}
                    />
                  </Field>
                </div>
              </Section>
            )}

            {action === "lead.release" && (
              <Section title="The release" description="Why the hold ended, as the lead's dial list row records it.">
                <Field label="Reason" htmlFor="trigger-reason" hint="released when left empty: no_show, cancelled.">
                  <Input className="font-mono" value={values.reason} onChange={(e) => set("reason", e.target.value)} />
                </Field>
              </Section>
            )}

            {updatesLead && (
              <Section
                title="The contact's fields"
                description="Answers copied from the request into the contact's fields, for a lead the platform already has. A value the request carries replaces the one on the form, and a blank or missing one changes nothing. Who the lead is (the name, the phone, consent) is not changed here."
              >
                <div className="space-y-2" data-testid="trigger-form-fields">
                  <p className="text-xs text-text-muted">
                    Each key the sender posts can fill one field; the keys are this trigger's newest request's, in the
                    sender's order. Point the sender at the URL first, with "Only keep what arrives" or with one field
                    mapped, to see what it sends.
                  </p>
                  <FieldMapper
                    value={values.fields}
                    onChange={(next) => set("fields", next)}
                    original={originalFields}
                    doc={frozen?.doc}
                    request={frozen?.state}
                    design={design.isError ? NO_DESIGN : design.data}
                    uses={uses}
                  />
                </div>
              </Section>
            )}

            {action === "lead.note" && (
              <Section
                title="The note"
                description="What the sender wrote goes on the lead's record as a note, with no person named as its author. A supervisor can edit or remove it."
              >
                {pathField("note", true, "note")}
              </Section>
            )}

            {action === LEAD_OUTCOME && (
              <Section
                title="The outcomes it may set"
                description="A sender can set only the outcomes ticked here. Each does what it does when somebody sets it on the lead's record: the stage, the dial lists, a hold, a do-not-call. Tick as few as the sender needs."
              >
                <OutcomePicker
                  picked={values.outcomes}
                  catalog={catalog.data}
                  failed={catalog.isError}
                  waiting={tenant === null}
                  onChange={(next) => set("outcomes", next)}
                />
                {pathField("outcome", values.outcomes.length > 1, "outcome")}
                <p className="-mt-2 text-xs text-text-muted">
                  {values.outcomes.length > 1
                    ? "With more than one ticked, each request has to say which."
                    : "With one ticked, every request sets it, and this can stay empty."}
                </p>
                {pathField("note", false, "note")}
                <p className="-mt-2 text-xs text-text-muted">
                  Kept with the outcome, with no person named as its author. An outcome that needs a note is not set
                  without one.
                </p>
                {pathField("callback_at", false, "appointment.start_time")}
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Times read in" htmlFor="trigger-timezone" hint="For a time with no zone.">
                    <Input
                      className="font-mono"
                      placeholder="America/Los_Angeles"
                      value={values.timezone}
                      onChange={(e) => set("timezone", e.target.value)}
                    />
                  </Field>
                </div>
                <p className="-mt-2 text-xs text-text-muted">
                  A Callback is booked at this time, as a rep&apos;s would be, and the dialer waits for it. A time
                  that has passed, or is on another outcome, sets nothing.
                </p>
              </Section>
            )}

            {enlist && (
              <Section
                title="A new lead"
                description="A number the platform already has is that lead, as it is — a mapping is read only when the lead is being made."
              >
                {pathField("tags", false, "tags")}
                <p className="-mt-1 text-xs text-text-muted">
                  A firm the platform knows is read off the tags and kept on the lead as its agency,
                  with that firm's state; <span className="font-mono">spanish</span> among them makes
                  the lead Spanish, and its absence English. Anything named below wins over the tags.
                </p>
                <p className="-mt-1 text-xs text-text-muted">
                  A tag naming one of your dispositions becomes the lead's. When several do, the
                  outcome wins over the activity — a lead tagged both signed and chase is signed —
                  and a lead whose tags say the case is over joins the campaign already off its dial
                  list.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  {pathField("first_name")}
                  {pathField("last_name")}
                  {pathField("language")}
                  {pathField("state")}
                  {pathField("timezone")}
                </div>

                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-medium text-text">More about the lead</p>
                      <p className="text-xs text-text-muted">
                        Anything else a request carries, kept on the lead as a named attribute: an email, a case type.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => set("attributes", [...values.attributes, { name: "", path: "" }])}
                    >
                      <Plus />
                      Add one
                    </Button>
                  </div>
                  {values.attributes.map((row, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        aria-label={`Attribute ${index + 1}, name`}
                        autoComplete="off"
                        className="w-40 font-mono"
                        placeholder="email"
                        value={row.name}
                        onChange={(e) =>
                          set("attributes", values.attributes.map((r, i) => (i === index ? { ...r, name: e.target.value } : r)))
                        }
                      />
                      <span className="text-xs text-text-muted">from</span>
                      <Input
                        aria-label={`Attribute ${index + 1}, path`}
                        autoComplete="off"
                        className="min-w-0 flex-1 font-mono"
                        list={PATHS_LIST}
                        placeholder="contact.email"
                        value={row.path}
                        onChange={(e) =>
                          set("attributes", values.attributes.map((r, i) => (i === index ? { ...r, path: e.target.value } : r)))
                        }
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove attribute ${index + 1}`}
                        onClick={() => set("attributes", values.attributes.filter((_, i) => i !== index))}
                      >
                        <X />
                      </Button>
                    </div>
                  ))}
                </div>

                <div className="space-y-2" data-testid="trigger-form-fields">
                  <div>
                    <p className="text-xs font-medium text-text">Custom fields</p>
                    <p className="text-xs text-text-muted">
                      A new lead's answers, copied from the request into the contact's fields. Each key the sender posts
                      can fill one field; the keys are this trigger's newest request's, in the sender's order. A sender
                      that posts a field under its label, as GoHighLevel does, is matched by that label: spaces, quote
                      style and capitals need not be exact. Only when this trigger makes the lead.
                    </p>
                  </div>
                  <FieldMapper
                    value={values.fields}
                    onChange={(next) => set("fields", next)}
                    original={originalFields}
                    doc={frozen?.doc}
                    request={frozen?.state}
                    design={design.isError ? NO_DESIGN : design.data}
                    uses={uses}
                  />
                </div>

                <Field label="List name" htmlFor="trigger-list-name" hint="The campaign's Lists tab shows this trigger's leads under it. The trigger's name when left empty.">
                  <Input maxLength={200} value={values.list_name} onChange={(e) => set("list_name", e.target.value)} />
                </Field>
              </Section>
            )}

            {enlist && (
              <Section
                title="Firm and qualification"
                description="Which firm each new lead goes to, and which leads their answers rule out. A firm with a campaign of its own takes its leads there; the campaign above takes the rest."
              >
                {pathField("firm_id", false, "firm_id")}
                <TriggerRouting value={values.routing} onChange={(next) => set("routing", next)} enabled={open} />
              </Section>
            )}

            {/* A text is known by the provider's id for it, and a report by that id and its status:
                the mapping already names both. An email is known by its own Message-ID. */}
            {((!message && !report && !email) || values.idempotency.length > 0) && (
              <Section
                title="Sent twice"
                description="A request with the same Idempotency-Key header is kept once. Without one, these paths name the event, so a sender that fires twice with other bytes is still kept once. Name the event, not only the lead: a second booking for one lead is a new event."
              >
                {values.idempotency.map((path, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      aria-label={`Known by, path ${index + 1}`}
                      autoComplete="off"
                      className="min-w-0 flex-1 font-mono"
                      list={PATHS_LIST}
                      placeholder="appointment.id"
                      value={path}
                      onChange={(e) => set("idempotency", values.idempotency.map((p, i) => (i === index ? e.target.value : p)))}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Remove path ${index + 1}`}
                      onClick={() => set("idempotency", values.idempotency.filter((_, i) => i !== index))}
                    >
                      <X />
                    </Button>
                  </div>
                ))}
                {values.idempotency.length < MAX_IDEMPOTENCY_PATHS && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => set("idempotency", [...values.idempotency, ""])}
                  >
                    <Plus />
                    Add a path
                  </Button>
                )}
              </Section>
            )}

            <Section title="The secret" description="How a request carries the secret that proves who sent it.">
              <Field label="It travels" htmlFor="trigger-secret-kind" hint={secretKindText(values.secret_kind).description}>
                <Select value={values.secret_kind} onValueChange={(next) => set("secret_kind", next)}>
                  <SelectTrigger id="trigger-secret-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SECRET_KINDS.map((kind) => (
                      <SelectItem key={kind} value={kind} description={secretKindText(kind).description}>
                        {secretKindText(kind).label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {!basic && (
                <Field label="Header" htmlFor="trigger-secret-header" hint="Letters, digits and hyphens.">
                  <Input
                    autoComplete="off"
                    className="font-mono"
                    maxLength={100}
                    value={values.secret_header}
                    onChange={(e) => set("secret_header", e.target.value)}
                  />
                </Field>
              )}
            </Section>

            {trigger && (
              <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
                <div>
                  <p id="trigger-enabled-label" className="text-xs font-medium text-text">
                    Takes requests
                  </p>
                  <p className="text-xs text-text-muted">
                    Off, its URL answers 404 and nothing is kept. What it already received stays readable.
                  </p>
                </div>
                <Switch
                  checked={values.enabled}
                  onCheckedChange={(checked) => set("enabled", checked)}
                  aria-labelledby="trigger-enabled-label"
                />
              </div>
            )}

            <datalist id={PATHS_LIST}>
              {suggestions.map((s) => (
                <option key={s.path} value={s.path}>
                  {s.example}
                </option>
              ))}
            </datalist>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={create.isPending || update.isPending}>
              {trigger ? "Save changes" : "Add trigger"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The tenant's outcomes, each to tick: its name, its slug, and what setting it does to a lead. One
 * that is ticked and no longer offered is listed last, in words that say so, since core-api refuses
 * the list until it is unticked.
 */
function OutcomePicker({
  picked, catalog, failed, waiting, onChange,
}: {
  picked: readonly string[];
  catalog: readonly Disposition[] | undefined;
  failed: boolean;
  /** No tenant yet: a superadmin who has not chosen one. */
  waiting: boolean;
  onChange: (next: string[]) => void;
}) {
  if (waiting) return <p className="text-xs text-text-muted">Choose the tenant first: the outcomes are its own.</p>;
  if (failed && !catalog) {
    return <Callout tone="danger">The outcomes could not be read. Close this and open it again.</Callout>;
  }
  if (!catalog) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-4" />
      </div>
    );
  }
  const offered = catalog.filter((d) => d.active);
  const gone = outcomesGone(picked, offered);
  return (
    <div className="space-y-1.5" data-testid="trigger-form-outcomes">
      <ul
        aria-label="The outcomes this trigger may set"
        className="max-h-72 divide-y divide-border overflow-y-auto rounded-md border border-border scrollbar-subtle"
      >
        {offered.map((d) => (
          <li key={d.slug} className="flex items-start gap-3 px-3 py-2 transition-colors duration-200 has-[:checked]:bg-accent-2">
            <input
              id={`trigger-outcome-${d.slug}`}
              type="checkbox"
              className="mt-1 size-3.5 shrink-0 accent-accent-9"
              checked={picked.includes(d.slug)}
              onChange={() => onChange(toggleOutcome(picked, d.slug, offered))}
            />
            <label htmlFor={`trigger-outcome-${d.slug}`} className="min-w-0 flex-1 cursor-pointer">
              <span className="flex flex-wrap items-baseline gap-x-1.5">
                <span className="text-sm font-medium text-text">{d.label}</span>
                <span className="font-mono text-xs text-text-muted">{d.slug}</span>
              </span>
              <span className="block text-xs text-text-muted">{outcomeEffect(d)}</span>
            </label>
          </li>
        ))}
        {gone.map((slug) => (
          <li key={slug} className="flex items-start gap-3 bg-warning-2 px-3 py-2">
            <input
              id={`trigger-outcome-${slug}`}
              type="checkbox"
              className="mt-1 size-3.5 shrink-0 accent-accent-9"
              checked
              onChange={() => onChange(toggleOutcome(picked, slug, offered))}
            />
            <label htmlFor={`trigger-outcome-${slug}`} className="min-w-0 flex-1 cursor-pointer">
              <span className="font-mono text-xs text-text">{slug}</span>
              <span className="block text-xs text-warning-11">
                No longer one of the tenant's outcomes: a request that names it sets nothing. Untick it.
              </span>
            </label>
          </li>
        ))}
      </ul>
      <p className="text-xs text-text-muted">
        {picked.length === 0 ? "None ticked yet." : picked.length === 1 ? "1 ticked." : `${picked.length} ticked.`}
      </p>
    </div>
  );
}

function Section({
  title, description, children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3 border-t border-border pt-4">
      <div>
        <h3 className="text-sm font-medium text-text">{title}</h3>
        <p className="text-xs text-text-muted">{description}</p>
      </div>
      {children}
    </section>
  );
}
